import { plural } from './plural'

/** Strings for the topic view: header, agent strip and thread board (S4, K2). */
export const consoleCopy = {
  retry: 'Retry',
  you: 'you',
  age: {
    now: 'now',
    minutes: (n: number) => `${n.toLocaleString()}m`,
    hours: (n: number) => `${n.toLocaleString()}h`,
    days: (n: number) => `${n.toLocaleString()}d`,
  },
  agentState: {
    running: 'running',
    blocked: 'blocked',
    paused: 'paused',
    idle: 'idle',
  },
  threadStatus: {
    needs_you: 'needs you',
    active: 'active',
    resolved: 'resolved',
  },
  topic: {
    loadFailed: 'Could not load this topic.',
    people: (names: string) => `${names} in this topic`,
    views: 'Topic views',
    threads: 'Threads',
    resources: 'Resources',
    away: (n: number) =>
      plural(n, {
        one: '{n} thread updated while you were away',
        other: '{n} threads updated while you were away',
      }),
    viewLatest: 'View latest',
    agents: (n: number) => plural(n, { one: '{n} agent', other: '{n} agents' }),
    running: (n: number) => `${n.toLocaleString()} running`,
    blocked: (n: number) => `${n.toLocaleString()} blocked`,
    paused: (n: number) => `${n.toLocaleString()} paused`,
    primary: 'primary',
    noAgents: 'No agents in this topic.',
  },
  board: {
    filterGroup: 'Filter threads',
    filters: {
      all: 'All',
      needs_you: 'Needs you',
      active: 'Active',
      resolved: 'Resolved',
      mine: 'Mine',
    },
    countNoun: 'threads',
    loadFailed: 'Could not load threads.',
    empty: 'No threads yet',
    emptyHint: 'Post a message below to start one.',
    /** Names every active filter: the status chip and the keyword. */
    noMatch: (
      filter: 'all' | 'needs_you' | 'active' | 'resolved' | 'mine',
      query: string,
    ) => {
      if (!query) {
        return {
          all: 'No threads.',
          needs_you: 'No threads need you.',
          active: 'No active threads.',
          resolved: 'No resolved threads.',
          mine: 'No threads of yours.',
        }[filter]
      }
      return {
        all: `No threads match "${query}".`,
        needs_you: `No threads that need you match "${query}".`,
        active: `No active threads match "${query}".`,
        resolved: `No resolved threads match "${query}".`,
        mine: `No threads of yours match "${query}".`,
      }[filter]
    },
    clearFilters: 'Clear filters',
    searchLabel: 'Filter threads by keyword',
    searchPlaceholder: 'Filter threads',
    live: 'live',
    select: 'Select',
    selectAll: 'Select all',
    selected: (n: number) => `${n.toLocaleString()} selected`,
    archive: 'Archive',
    delete: 'Delete',
    archived: (n: number) =>
      plural(n, { one: '{n} thread archived', other: '{n} threads archived' }),
    undo: 'Undo',
    undoFailed: 'Failed to restore the archived threads — try again.',
    archiveFailed: (n: number) =>
      plural(n, {
        one: 'Failed to archive thread — try again.',
        other: 'Failed to archive threads — try again.',
      }),
    deleted: (n: number) =>
      plural(n, { one: '{n} thread deleted', other: '{n} threads deleted' }),
    deleteFailed: (n: number) =>
      plural(n, {
        one: 'Failed to delete thread — try again.',
        other: 'Failed to delete threads — try again.',
      }),
    confirmDelete: {
      title: (n: number) =>
        plural(n, { one: 'Delete {n} thread', other: 'Delete {n} threads' }),
      description: (n: number) =>
        plural(n, {
          one: 'Permanently deletes {n} thread and its messages.',
          other: 'Permanently deletes {n} threads and their messages.',
        }),
      confirm: 'Delete forever',
    },
    archivedSection: 'Archived',
    untitled: 'Untitled thread',
    restore: 'Restore',
    restored: 'Thread restored',
    restoreFailed: 'Failed to restore thread — try again.',
    deleteForever: 'Delete forever',
    confirmPurge: {
      title: 'Delete thread',
      description: 'Permanently deletes the thread and its messages.',
    },
    purged: 'Thread deleted',
    purgeFailed: 'Failed to delete thread — try again.',
    selectThread: (title: string) => `Select thread: ${title}`,
    moreParticipants: (n: number) => `+${n.toLocaleString()}`,
    replies: (n: number) =>
      plural(n, { one: '{n} reply', other: '{n} replies' }),
  },
} as const
